import React, { ReactNode, useMemo } from 'react';
import {
  ENTERPRISE_MEMBER_INVITATION_STATUS,
  EnterpriseMember,
  EnterpriseMemberInvitationStatus,
} from 'models/Enterprises';
import { ManageEnterprisePeopleTable } from '../../data';
import XcircleIcon from 'icons/Xcircle.icon';
import MailIcon from 'icons/Mail.icon';
import CheckcircleIcon from 'icons/Checkcircle.icon';
import classNames from 'classnames';
import ResolvePopup from 'components/ResolvePopup';
import { useTranslation } from 'react-i18next';
import utils, { normalizeEmail, removeLINK } from 'utils';
import { Checkbox } from 'ncoded-component-library';
import { NEW_WINDOW_PROPS } from 'data/global';
import { NameCell } from 'router/subrouters/Dash/subrouters/private/subrouters/Enterprise/pages/Team/components/TeamMembersTable/components/TeamMembersTableCells';
import { formatPhoneNumber } from 'phoneNumberUtils';

type CellProps = {
  member: EnterpriseMember;
  isSelectedMember?: boolean;
  config?: Record<string, string>;
  onSelectMember: (member: EnterpriseMember) => void;
};

const CheckboxCell: React.FC<CellProps> = ({
  member,
  isSelectedMember,
  onSelectMember,
}) => (
  <div onClick={(e) => e.stopPropagation()}>
    <Checkbox
      checked={isSelectedMember}
      onChange={() => onSelectMember?.(member)}
    />
  </div>
);

const SeniorityCell: React.FC<CellProps> = ({ member }) => {
  const { seniority } = member || {};

  return <p>{seniority}</p>;
};

const TitleCell: React.FC<CellProps> = ({ member }) => {
  const { title } = member || {};

  return <p>{title}</p>;
};

const BusinessCell: React.FC<CellProps> = ({ member }) => {
  const { businessUnitName } = member || {};

  return <p>{businessUnitName ? removeLINK(businessUnitName) : '/'}</p>;
};

const LocationCell: React.FC<CellProps> = ({ member }) => {
  const { location } = member || {};

  return <p>{location}</p>;
};

const InvitedCell: React.FC<CellProps> = ({ member }) => {
  const { invitationStatus } = member || {};

  const { t } = useTranslation();

  const ICON: Record<EnterpriseMemberInvitationStatus, JSX.Element> = useMemo(
    () => ({
      [ENTERPRISE_MEMBER_INVITATION_STATUS.ACTIVE]: <CheckcircleIcon />,
      [ENTERPRISE_MEMBER_INVITATION_STATUS.ACCEPTED]: <CheckcircleIcon />,
      [ENTERPRISE_MEMBER_INVITATION_STATUS.INVITATION_SENT]: <MailIcon />,
      [ENTERPRISE_MEMBER_INVITATION_STATUS.NOT_INVITED]: <XcircleIcon />,
    }),
    [],
  );

  return (
    <ResolvePopup onHover popupContent={<span>{t(invitationStatus)}</span>}>
      <div className={classNames('invited-cell', invitationStatus)}>
        {ICON[invitationStatus]}
      </div>
    </ResolvePopup>
  );
};

const EmailCell: React.FC<CellProps> = ({ member }) => {
  const { email } = member || {};

  if (!email) return null;

  return <p className="email-cell">{normalizeEmail(email)}</p>;
};

const MobileCell: React.FC<CellProps> = ({ member }) => {
  const formattedPhone = formatPhoneNumber(member?.user?.phone);

  if (!formattedPhone) return null;

  return <p>{formattedPhone}</p>;
};

const LinkedInCell: React.FC<CellProps> = ({ member }) => {
  const { user } = member || {};
  const { linkedinAccount } = user || {};

  if (!linkedinAccount) return null;

  const { linkedinUrl, vanityName } =
    utils.getLinkedinUrlAndVanityName(linkedinAccount);

  return (
    <a href={linkedinUrl} {...NEW_WINDOW_PROPS}>
      {vanityName}
    </a>
  );
};

export const BODY_CONTENT_GENERATE: Record<
  keyof ManageEnterprisePeopleTable,
  (cellProps: CellProps) => ReactNode
> = {
  checkbox: (cellProps: CellProps) => <CheckboxCell {...cellProps} />,
  name: (cellProps: CellProps) => <NameCell {...cellProps} />,
  seniority: (cellProps: CellProps) => <SeniorityCell {...cellProps} />,
  title: (cellProps: CellProps) => <TitleCell {...cellProps} />,
  businessUnitName: (cellProps: CellProps) => <BusinessCell {...cellProps} />,
  location: (cellProps: CellProps) => <LocationCell {...cellProps} />,
  invited: (cellProps: CellProps) => <InvitedCell {...cellProps} />,
  email: (cellProps: CellProps) => <EmailCell {...cellProps} />,
  mobile: (cellProps: CellProps) => <MobileCell {...cellProps} />,
  linkedIn: (cellProps: CellProps) => <LinkedInCell {...cellProps} />,
};
